import clsx from "clsx";
import {useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import Copy from "@/components/ask/Copy";
import {TooltipProvider} from "@/components/ui/tooltip";
import {DEFAULT_BRAND} from "@/enums/brands";
import {THEME} from "@/enums/profile";
import {
   GptColors,
   getAssistantContentStyle,
   getUserBubbleStyle,
} from "@/utils/chat/gptColors";
import {GetBackgroundColor, GetFontColors} from "@/utils/library/color";
import BrandImage from "../common/BrandImage";
import GailThinkingWords from "../chat/GailThinkingWords";

const AskChatBubble: React.FC<{
   children?: React.ReactNode;
   isUser?: boolean;
   isWelcomeMessage?: boolean;
   agentName?: string;
   themeOverride?: THEME;
   brandOverride?: string;
   className?: string;
   isNewGailGpt?: boolean;
   showCopy?: boolean;
   copyText?: string;
   isLastInGroup?: boolean;
   readAloudSlot?: React.ReactNode;
   feedbackSlot?: React.ReactNode;
   colors?: GptColors;
   assistantImageUrl?: string;
}> = ({
   children,
   isUser,
   isWelcomeMessage,
   agentName,
   themeOverride,
   brandOverride,
   className,
   isNewGailGpt = false,
   showCopy = false,
   copyText,
   isLastInGroup = true,
   readAloudSlot,
   feedbackSlot,
   colors,
   assistantImageUrl,
}) => {
   const {t} = useTranslation("ask");
   const contentRef = useRef<HTMLDivElement>(null);

   const bubbleColors = useMemo(() => {
      const color = GetBackgroundColor(brandOverride);
      const fontColors = color
         ? GetFontColors({primary: color.primary, secondary: color.primary})
         : {primary: "#000", secondary: "#000"};
      return {
         background: color?.primary || "#eee",
         font: fontColors.primary,
      };
   }, [themeOverride, brandOverride]);

   const userStyle = isUser
      ? getUserBubbleStyle(colors, bubbleColors.background, bubbleColors.font)
      : undefined;
   const assistantContentStyle = isUser ? undefined : getAssistantContentStyle(colors);
   const userContentStyle = isUser && colors?.chatBubble?.text ? {color: colors.chatBubble.text} : undefined;

   return (
      <div
         className={clsx(
            "text-sm",
            "monument-font",
            "text-900",
            "relative",
            // Assistant: block for overflow-x-auto scroll; user: inline-block
            isUser ? "inline-block rounded-lg max-w-[90%]" : "block w-full",
            // Spacing
            {
               "mb-6": isUser && isLastInGroup,
               "mb-8": !isUser,
               "mb-0": isUser && !isLastInGroup,
            },
            className,
            {
               "ml-auto px-3 py-2": isUser,
            }
         )}
         style={{
            backgroundColor: userStyle?.backgroundColor,
            color: userStyle?.color,
         }}
      >
         {!isUser && !isWelcomeMessage && (
            <div className="flex items-center mb-3">
               {assistantImageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                     src={assistantImageUrl}
                     alt=""
                     className="inline h-4 w-4 rounded-full object-cover"
                  />
               ) : (
                  <BrandImage
                     brand={brandOverride ?? DEFAULT_BRAND}
                     className="inline"
                     height={16}
                     variant="small"
                  />
               )}
               <span
                  className={clsx("font-medium", "ml-2", "text-sm", "text-foreground")}
                  style={assistantContentStyle}
               >
                  <span className="mr-1">•</span>
                  {agentName ? ` ${agentName}` : ` ${t("agent")}`}
               </span>
            </div>
         )}
         <div
            className={clsx("text-foreground", {"text-white": isUser})}
            style={userContentStyle ?? assistantContentStyle}
            ref={contentRef}
         >
            {children}
         </div>
         {!!children && !isUser && isNewGailGpt && (showCopy || readAloudSlot || feedbackSlot) && (
            <TooltipProvider delayDuration={500} skipDelayDuration={100}>
               <div className="mt-3 flex flex-wrap items-start gap-2 text-600">
                  {showCopy && <Copy containerRef={contentRef} copyText={copyText} />}
                  {readAloudSlot}
                  {feedbackSlot}
               </div>
            </TooltipProvider>
         )}
         {!children && (
            <div className="text-foreground">
               {isNewGailGpt ? (
                  <GailThinkingWords />
               ) : (
                  <span className="inline-block animate-pulse">▋</span>
               )}
            </div>
         )}
      </div>
   );
};

export default AskChatBubble;
